/**
 * FAQ "More" toggle.
 *
 * Progressive enhancement, in the strict sense: the section renders every
 * question and the button ships `hidden`. Only when this element upgrades does
 * it add `is-collapsible` — which is what the stylesheet's hide rule is scoped
 * to — and reveal the button. So with JavaScript off, or before this file
 * arrives, the visitor sees the whole FAQ rather than three questions and a
 * button that does nothing.
 *
 * The <details> elements are untouched. Each one still opens, closes and
 * announces itself on its own; this only decides how many are on screen.
 */

class FaqMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-faq-more]');
    if (!this.button) return;

    this.label = this.querySelector('[data-faq-more-label]');
    this.expanded = false;

    this.moreLabel = this.label?.textContent.trim() || 'More questions';
    this.lessLabel = this.dataset.lessLabel || 'Show fewer';

    this.classList.add('is-collapsible');
    this.button.hidden = false;
    this.sync();

    this.button.addEventListener('click', () => {
      this.expanded = !this.expanded;
      this.sync();

      // Collapsing with a hidden question still open would leave the visitor
      // looking at a list that just got shorter for no visible reason. Closing
      // them makes the change legible.
      if (!this.expanded) {
        this.querySelectorAll('.faq-item.is-overflow[open]').forEach((d) => {
          d.open = false;
        });
        // The button has just moved up the page; without this the viewport can
        // end up below the section entirely.
        const top = this.getBoundingClientRect().top;
        if (top < 0) this.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    });
  }

  sync() {
    this.classList.toggle('is-expanded', this.expanded);
    this.button.setAttribute('aria-expanded', String(this.expanded));
    if (this.label) this.label.textContent = this.expanded ? this.lessLabel : this.moreLabel;
  }
}

customElements.define('faq-more', FaqMore);
